import type { Navigator, Screen } from "../app/screen";
import { Api } from "../net/api";
import { pageShell, note } from "../ui/page";

/**
 * RANKS - the standings.
 *
 * 🚨 **THIS SCREEN COULD NOT EXIST UNTIL THE GAME REMEMBERED ANYTHING.** It was a stub for a real
 * reason, and the reason was two defects deep: nothing ever WROTE `Player__Statistics`
 * (`OnMatchEnded` only handed out awards and returned early on a loss), and `Player/Statistics`
 * never READ it either - the route fell through to a JSON capture of the original studio's server,
 * so every account showed the same fixed zeros. A leaderboard over that would have been a table of
 * identical rows presented as a ladder.
 *
 * ⭐ **THE ORDER IS WINS, NOT RATING, AND THAT IS DELIBERATE.** Nothing writes an elo yet - the solo
 * modes carry no league - so ranking by it would sort every player by the same starting number.
 * When ranked play exists, the rating becomes the order and this comment is the record of why.
 *
 * ⚠️ **ONLY PLAYERS WHO HAVE FINISHED A MATCH APPEAR.** A board listing accounts that have never
 * played is a user directory wearing a ladder's clothes.
 */

interface Row {
  rank?: number;
  userId?: string;
  userName?: string;
  games?: number;
  wins?: number;
  losses?: number;
}

export const RanksScreen: Screen = {
  id: "ranks",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "STANDINGS", "who is winning");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "ranks";
    shell.body.append(root);
    root.append(note("Reading the standings..."));

    let rows: Row[] = [];
    let me = "";
    try {
      const [board, ud] = await Promise.all([
        Api.standings() as Promise<Row[]>,
        Api.userData() as Promise<{ id?: string }>,
      ]);
      rows = Array.isArray(board) ? board : [];
      me = String(ud?.id ?? "");
    } catch (e) {
      root.replaceChildren(note(`Could not read the standings: ${describe(e)}`, true));
      return;
    }

    root.replaceChildren();

    if (!rows.length) {
      // ⚠️ An empty board is a FACT with a cause, and saying the cause is the difference between
      // "this screen is broken" and "nobody has finished a match yet".
      root.append(note("Nobody has finished a match yet. Play one and you will be first."));
      return;
    }

    const head = document.createElement("div");
    head.className = "ranks__row ranks__row--head";
    head.append(
      cell("#", "ranks__rank"),
      cell("PLAYER", "ranks__who"),
      cell("WINS", "ranks__n"),
      cell("LOSSES", "ranks__n"),
      cell("PLAYED", "ranks__n"),
    );
    root.append(head);

    for (const r of rows) {
      const row = document.createElement("div");
      row.className = "ranks__row";
      // ⭐ Your own line is marked. A ladder you cannot find yourself on is a list of strangers.
      if (me && r.userId === me) row.classList.add("ranks__row--me");
      row.append(
        cell(String(r.rank ?? ""), "ranks__rank"),
        // ⚠️ textContent inside `cell` - another player's name is untrusted text.
        cell(String(r.userName ?? r.userId ?? "Unknown"), "ranks__who"),
        cell(String(r.wins ?? 0), "ranks__n"),
        cell(String(r.losses ?? 0), "ranks__n"),
        cell(String(r.games ?? 0), "ranks__n"),
      );
      root.append(row);
    }

    root.append(note("Ordered by wins. Ratings begin when ranked duels do."));
  },
};

function cell(text: string, cls: string): HTMLElement {
  const s = document.createElement("span");
  s.className = cls;
  s.textContent = text;
  return s;
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
